/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from 'react';
import * as d3 from 'd3';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import AppTypes from '@avtechsoftware/ra-javascript-utilities/src/types/AppTypes';
import { OperatorTypeDetails } from '@avtechsoftware/ra-javascript-utilities/src/types/OperatorTypes';
import D3ChannelGraphingUtil from './D3ChannelGraphingUtil';
import D3DrawThresholdOverlay from './D3DrawThresholdOverlay';
import D3Utility from './D3Utility';

function MultiLineGraph({
  dataSets,
  temperatureScale,
  threshold,
  startTime,
  endTime,
  timeZone,
  channelKeyObject,
  customUnits,
  width,
  height,
}) {
  const hasThreshold = threshold
      && threshold.operator
      && (threshold.value !== null && threshold.value !== undefined);

  const channel = ChannelService.getChannelGroupChannelByAppType(
    channelKeyObject.channelGroupType,
    channelKeyObject.channelIndex,
    channelKeyObject.channelType,
    AppTypes.CLOUD,
  );

  const operatorType = hasThreshold
    ? Object.keys(OperatorTypeDetails).find(
      (type) => OperatorTypeDetails[type].symbol === threshold.operator,
    )
    : null;

  const drawThresholdOverlay = operatorType ? D3DrawThresholdOverlay[operatorType] : null;

  const graphUtilities = D3ChannelGraphingUtil.getUtilitySetByChannel(channel);

  const getPopupDisplay = (timestamp, value) => `${D3Utility.getTimeZoneDate(timeZone, timestamp)}  | ${channel.getDisplayValueWithUnits(value, { scale: temperatureScale, customUnits })}`;

  const graphRef = useRef(null);

  useEffect(() => {
    d3.select(graphRef.current).html('');

    const marginTop = 20;
    const marginRight = 20;
    const marginBottom = 30;
    const marginLeft = graphUtilities.getMarginLeft({
      trueDisplayValue: channel.trueDisplayValue,
      falseDisplayValue: channel.falseDisplayValue,
    });

    const svg = d3
      .select(graphRef.current)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMaxYMid');
      // .attr('preserveAspectRatio', 'xMidYMid meet');

    // --- COMBINE all dataset points for y-axis ---
    const allPoints = dataSets.flatMap((s) => s.data);

    // ----- X AXIS -----
    const xAxis = graphUtilities.getXAxis(
      startTime,
      endTime + 21600,
      width,
      marginLeft,
      marginRight,
    );
    const tickCount = Math.max(Math.floor(width / 100), 2);
    xAxis.ticks(tickCount);

    graphUtilities.drawXAxis(svg, xAxis, height, marginTop, marginBottom, timeZone);

    if (width < 500) {
      svg.selectAll('.x-axis text')
        .attr('transform', 'rotate(-40)')
        .style('text-anchor', 'end');
    }
    // ----- Y AXIS -----
    const values = allPoints.map((d) => d.value);
    let [min, max] = d3.extent(values);

    if (min === max) {
      min -= 1;
      max += 1;
    }

    const yAxis = graphUtilities.getYAxis(
      allPoints,
      height,
      marginBottom,
      marginBottom,
      {
        temperatureScale,
        trueDisplayValue: channel.trueDisplayValue,
        falseDisplayValue: channel.falseDisplayValue,
        ...(hasThreshold && {
          thresholdValue1: threshold.value,
          thresholdValue2: threshold.value2,
        }),
        domainOverride: [min, max],
      },
    );

    graphUtilities.drawYAxis(svg, yAxis, marginLeft);
    graphUtilities.drawYAxisGrid(svg, yAxis, width, marginLeft, marginRight);

    const overlayWidth = width - marginRight - marginLeft;

    if (hasThreshold && drawThresholdOverlay) {
      drawThresholdOverlay(
        svg,
        threshold,
        height,
        marginLeft,
        overlayWidth,
        marginBottom,
        yAxis,
        {
          trueDisplayValue: channel.trueDisplayValue,
          falseDisplayValue: channel.falseDisplayValue,
        },
      );
    }

    const div = d3.select(graphRef.current).append('div')
      .attr('class', 'popup')
      .style('opacity', 0);

    // eslint-disable-next-line no-unused-vars
    dataSets.forEach(({ id, data, color }) => {
      if (data.length === 0) return;
      const line = graphUtilities.getDataLine(xAxis, yAxis, {
        temperatureScale,
        trueDisplayValue: channel.trueDisplayValue,
        falseDisplayValue: channel.falseDisplayValue,
      });

      svg
        .append('path')
        .datum(data)
        .attr('class', 'line')
        .attr('d', line)
        .style('stroke', color || '#000')
        .style('stroke-width', '2px')
        .attr('fill', 'none');

      graphUtilities.drawMouseoverPoints(
        svg,
        data,
        xAxis,
        yAxis,
        div,
        marginLeft,
        marginTop,
        getPopupDisplay,
        {
          temperatureScale,
          trueDisplayValue: channel.trueDisplayValue,
          falseDisplayValue: channel.falseDisplayValue,
        },
      );
    });
  }, [
    dataSets,
    width,
    height,
    temperatureScale,
    threshold,
    timeZone,
    customUnits]);

  return <div id="Graph" ref={graphRef} />;
}

export default MultiLineGraph;
